import { type JSX, type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';

// The `/schema` subpath, not the package root.
//
// Same single declaration the dashboard edits — two copies of "which slots
// exist" diverge the first time somebody adds a heading. But the package root
// pulls in `@jt/observability`, which constructs an `AsyncLocalStorage`; in a
// browser bundle that throws on load and takes the entire homepage down. This
// entry point compiles to data with no runtime imports.
import { HOMEPAGE_BLOCKS } from '@jt/platform-content/schema';

/**
 * Editable copy, fetched from the platform, falling back to the bundle.
 *
 * ## The page renders before the request resolves, and that is deliberate
 *
 * Initial state is the copy shipped in the bundle — the real sentences, not
 * placeholders — so the homepage paints complete on the first frame. The fetch
 * then overrides anything an editor has changed.
 *
 * That ordering is what makes the site survive the content service being down,
 * slow, or not yet deployed: the worst case is that an edit is not shown, and
 * the best case costs nothing because there is never an empty state to flash.
 *
 * A failed request is deliberately silent. A marketing page must not show an
 * error because a CMS is unreachable; the shipped copy is a correct page.
 */

export type ContentKey = (typeof HOMEPAGE_BLOCKS)[number]['key'];

const FALLBACKS: Readonly<Record<string, string>> = Object.fromEntries(
  HOMEPAGE_BLOCKS.map((block) => [block.key, block.fallback]),
);

const ContentContext = createContext<Readonly<Record<string, string>>>(FALLBACKS);

export function ContentProvider({
  children,
  initial,
}: {
  readonly children: ReactNode;
  /** Lets tests supply content without a network round trip. */
  readonly initial?: Readonly<Record<string, string>>;
}): JSX.Element {
  const [blocks, setBlocks] = useState<Readonly<Record<string, string>>>(() => initial ?? FALLBACKS);

  useEffect(() => {
    if (initial !== undefined) return;

    const controller = new AbortController();

    void fetch('/api/v1/content/homepage', {
      signal: controller.signal,
      headers: { accept: 'application/json' },
    })
      .then(async (response) => (response.ok ? await response.json() : undefined))
      .then((payload: { data?: { blocks?: Record<string, string>; styles?: string } } | undefined) => {
        const fetched = payload?.data?.blocks;
        if (fetched !== undefined) {
          // Merged over the fallbacks rather than replacing them: a response
          // missing a key must not blank that slot on the page.
          setBlocks({ ...FALLBACKS, ...fetched });
        }

        applyPublishedStyles(payload?.data?.styles);
      })
      .catch(() => {
        // Silent on purpose. The page is already correct.
      });

    return () => controller.abort();
  }, [initial]);

  return <ContentContext.Provider value={blocks}>{children}</ContentContext.Provider>;
}

/**
 * Reads one editable string.
 *
 * Falls back twice — to the bundle's copy, then to the key — so a component can
 * never render `undefined` into the page.
 */
export function useContent(): (key: ContentKey) => string {
  const blocks = useContext(ContentContext);
  return useMemo(() => (key: ContentKey) => blocks[key] ?? FALLBACKS[key] ?? key, [blocks]);
}

/**
 * An edited value, or `undefined` when nobody has changed this block.
 *
 * `useContent` cannot answer that question: it returns the shipped fallback for
 * an unedited block, which is indistinguishable from someone typing that exact
 * string back in. Several places need the distinction — a translated string, a
 * caller's own children, a locale-specific sentence — because they must keep
 * their own behaviour until an editor genuinely overrides them.
 *
 * The footer's copyright line is the case that forced this. It drops the GPhC
 * claim outside the United Kingdom, because the GPhC is a British regulator;
 * treating the shipped English as "an edit" would have made every locale show
 * the British wording.
 */
export function useContentOverride(): (key: ContentKey) => string | undefined {
  const content = useContent();
  return useMemo(
    () => (key: ContentKey) => {
      const value = content(key);
      return value === FALLBACKS[key] ? undefined : value;
    },
    [content],
  );
}

/**
 * Applies the published design.
 *
 * ## Why a stylesheet and not inline styles
 *
 * The design is a set of rules against selectors, and the elements they target
 * are rendered by ten different components. Threading a style object into each
 * one would mean every component knowing about the design system; one
 * stylesheet means none of them do.
 *
 * ## Why appended, and why it is safe
 *
 * It is appended to `<head>` after the application's own stylesheet, so equal
 * specificity resolves in its favour and an editor's choice actually takes
 * effect — including over the responsive rules, which is the point of letting
 * someone set a size.
 *
 * The text is not arbitrary. Selectors come from a registry in the repository
 * and never from storage, and every value was matched against a per-kind
 * allow-list on the server, which re-checks on the way out. An empty or
 * unrecognised value produces no declaration at all, so a site nobody has
 * restyled receives an empty stylesheet and renders exactly as it does today.
 */
const STYLE_ELEMENT_ID = 'jt-published-design';

function applyPublishedStyles(css: string | undefined): void {
  if (typeof document === 'undefined') return;

  const existing = document.getElementById(STYLE_ELEMENT_ID);

  if (css === undefined || css.trim() === '') {
    existing?.remove();
    return;
  }

  const element =
    existing ??
    (() => {
      const created = document.createElement('style');
      created.id = STYLE_ELEMENT_ID;
      document.head.append(created);
      return created;
    })();

  element.textContent = css;
}

/**
 * The URL an image block's value resolves to, or `undefined`.
 *
 * The shape is checked, not just the emptiness. `useContent` returns the key
 * itself for a key it does not know, so a typo in a `contentKey` would
 * otherwise become an image request for `news.article1.image` — three broken
 * images on the homepage, from a mistake the type system cannot see. Asset ids
 * are 32 hex characters, which nothing else in the content model looks like.
 */
const ASSET_ID = /^[0-9a-f]{32}$/;

export function mediaSrc(assetId: string): string | undefined {
  return ASSET_ID.test(assetId) ? `/api/v1/content/media/${assetId}` : undefined;
}
